import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {css} from 'aphrodite';
import {findIconDefinition} from '@fortawesome/fontawesome-svg-core';
import {ToolTipCell} from 'components/ToolTipCell';
import './styles.scss';
import {CoverageSummary as CoverageSummaryNew} from '../Coverage/Cell/CoverageSummary/index';
import {DocumentsCell} from '../Coverage/Cell/DocumentsCell';
import {HighlightRowCell} from '../Coverage/Cell/HighlightRowCell';
import {PdfLink} from '../Coverage/Cell/PdfLink';
import {createColumnHelper} from '@tanstack/react-table';
import {
  faInfoCircle
} from '@fortawesome/fontawesome-pro-solid';

const columnHelper = createColumnHelper();

/**
 * Resolves which column gets the default sort for the DynamicCoverage table.
 *
 * Site JSON: optional `settings.table.defaultSort`: `{ columnProperty, direction?: "asc"|"desc" }`
 * (`columnProperty` must match a column's `property` and that column must be `sortable`). If
 * `defaultSort` is absent or invalid, uses the first sortable column in `table.columns` order,
 * ascending.
 *
 * Maps to `columnDef.initialSort` for TabularDataBasic/Header (false = asc, true = desc).
 *
 * @param {Object} tableConfig - `settings.table`
 * @returns {{ targetProperty: string|null, initialSort: boolean|undefined }}
 */
export const applyDefaultSortOrder = (tableConfig) => {
  const columns = tableConfig?.columns ?? [];
  const defaultSort = tableConfig?.defaultSort;

  if (
    defaultSort &&
    typeof defaultSort === 'object' &&
    defaultSort.columnProperty != null &&
    defaultSort.columnProperty !== ''
  ) {
    const match = columns.find((c) => c.property === defaultSort.columnProperty);
    if (match?.sortable) {
      const dir = String(defaultSort.direction ?? 'asc').toLowerCase();
      return {
        targetProperty: match.property,
        initialSort: dir === 'desc',
      };
    }

  }

  const firstSortable = columns.find((c) => c.sortable);
  if (firstSortable) {
    return {targetProperty: firstSortable.property, initialSort: false};
  }

  return {targetProperty: null, initialSort: undefined};
};

const planTypeHeaderRender = ({settings, styleSheet, label}) => {
  if (settings.content.planTypeHelpHover.length) {
    return (
      <span>
        {label}
        <ToolTipCell
          text={settings.content.planTypeHelpHover}
          placeholder={(<FontAwesomeIcon data-testid="plantype-tooltip" icon={faInfoCircle}/>)}
          placement="bottom"
          styleSheet={styleSheet}
          id="plantype-tooltip"
          ariaLabel={'More information about plan types'}
        />
      </span>
    )
  }

  return label;
}

export const getDynamicCoverageColumns = ({props}) => {
  const {settings, styleSheet} = props;
  const {targetProperty, initialSort: defaultInitialSort} = applyDefaultSortOrder(
    settings.table
  );

  return settings.table.columns.map((column) => {
    const styleName = `tableColumn${column.property}`;
    const cellClass = styleSheet[styleName] ? css(styleSheet[styleName]) : '';

    const isDefaultSortColumn =
      defaultInitialSort !== undefined &&
      targetProperty != null &&
      column.property === targetProperty;

    const dataTableColumn = {
      enableSorting: column.sortable,
      enableColumnFilter: false,
      sortDescFirst: false,
      // initialSort: applied on mount in TabularDataBasic/Header (see JSDoc on applyDefaultSortOrder)
      ...(isDefaultSortColumn && {initialSort: defaultInitialSort}),
      meta: {className: cellClass},
      size: 0,
      header: () => {
        if (column.property === 'planType') {
          return planTypeHeaderRender({...props, label: column.label});
        }

        return column.label;
      },
      cell: renderDynamicColumnCell(
        column,
        props
      )
    };

    return columnHelper.accessor(column.property, dataTableColumn);
  })
}

/**
 * Renders a dynamic column cell based on the column type
 * @param {Object} column - The column object
 * @param {Object} props - The props object
 * @returns {JSX.Element} The rendered column cell
 */
export const renderDynamicColumnCell = (
  column,
  props
) => {
  return ({cell, row}) => {
    const value = cell.getValue();
    const rowData = row.original;

    // Auto-detect type if not specified
    const type = column.type || (Array.isArray(value) ? 'array' : typeof value === 'object' ? 'object' : 'string');

    switch (type) {
      case 'array':
        return getArrayCellRender({...props, value, rowData, column, meta: column.meta || {separator: ', '}});
      case 'object':
        return getObjectCellRender({...props, value, rowData, column});
      case 'coverageSummary':
        return getCoverageSummaryTypeCellRender({...props, value, rowData, column});
      case 'documents':
        return getDocumentsCellRender({...props, value, column});
      case 'contacts':
        return getNewContactsCellRender({...props, value, rowData, column});
      case 'highlightRow':
        return getHighlightRowCellRender({...props, value, rowData, column});
      case 'customDisplay':
        return getCustomDisplayCellRender({...props, value, column});
      case 'pdfLink':
        return getPdfLinkCellRender({...props, value, rowData, column});
      case 'string':
        return value == null ? '' : String(value);
      default:
        return ''
    }
  }
}

/**
 * Renders a custom display cell based on the column type
 * @param {Object} value - The value of the cell
 * @param {Object} column - The column object
 * @returns {JSX.Element} The rendered cell
 */
export const getCustomDisplayCellRender = ({value, column}) => {
  const customDisplay = column.meta?.customDisplay || {};

  return customDisplay.mappings?.[value] ?? value;
}

/**
 * Renders an array cell based on the column type
 */
export const getArrayCellRender = ({styleSheet, rowData, value, column, meta}) => {
  if (!Array.isArray(value) || value.length === 0) {
    return '';
  }

  const separator = meta?.separator || ', ';

  // Handle array of objects (e.g., documentTypes with {id, name})
  const displayValue = value.length > 0 && typeof value[0] === 'object' && value[0].name
    ? value.map((item) => item.name).join(separator)
    : value.join(separator);

  if (meta?.tooltip && value.length > 5) {
    const tooltipId = column?.property ? `tooltip-${rowData.id}-${column.property}` : `tooltip-${rowData.id}`;
    return (
      <ToolTipCell
        text={displayValue}
        placement={'bottom'}
        styleSheet={styleSheet}
        placeholder={`${displayValue.slice(0, 20)} ...`}
        id={tooltipId}
        ariaLabel={displayValue}
      />
    );
  }

  return (<span>{displayValue}</span>);
}

/**
 * Renders an object cell based on the column type
 */
export const getObjectCellRender = ({value, rowData, column}) => {
  if (!value || typeof value !== 'object') {
    return '';
  }

  // Handle array of objects (like documentTypes)
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '';
    }
    if (value[0]?.name) {
      // Extract names from objects
      return <span>{value.map((item) => item.name).join(', ')}</span>;
    }
  }

  // For plain objects, stringify
  return <span>{JSON.stringify(value)}</span>;
}

/**
 * Renders a link cell based on the column type
 */
export const getDocumentsCellRender = ({settings, styleSheet, value, column, rowData}) => {
  return <DocumentsCell
    settings={settings}
    styleSheet={styleSheet}
    value={value}
    column={column}
    rowData={rowData}
  />;
}

/**
 * Renders a new contacts cell based on the column type
 * @param {Object} value - The value of the cell
 * @param {Object} column - The column object
 * @param {Object} rowData - The row data
 * @param {Object} styleSheet - The style sheet object
 * @returns {JSX.Element} The rendered cell
 */
export const getNewContactsCellRender = ({value, column, rowData, styleSheet}) => {
  const contacts = column.meta?.contacts?.map((contactMeta) => {
    const icon = findIconDefinition({prefix: contactMeta.icon.prefix, iconName: contactMeta.icon.name});

    // Try to find contact from rowData.fields if it exists, otherwise try rowData[contactMeta.name]
    let contactValue = null;
    if (rowData.fields && Array.isArray(rowData.fields)) {
      const contact = rowData.fields.find((field) => field.fieldName === contactMeta.name);
      contactValue = contact?.value;
    } else if (rowData[contactMeta.name] !== undefined) {
      // If fields array doesn't exist, try accessing directly from rowData
      contactValue = rowData[contactMeta.name];
    }

    // Add tooltip
    return (
      <ToolTipCell
        key={contactMeta.name}
        text={contactMeta.title}
        placement={'bottom'}
        styleSheet={styleSheet}
        placeholder={
          <span key={contactMeta.name}>
            <FontAwesomeIcon icon={icon} flip="horizontal"/>
            {contactValue ? contactValue : contactMeta.default}<br/>
          </span>
        }
        id={`contact-tooltip-${contactMeta.name}`}
      />
    );
  });

  return (
    <div key="contacts">
      {contacts}
    </div>
  );
}

/**
 * Renders a coverage summary type cell based on the column type
 */
export const getCoverageSummaryTypeCellRender = ({value, rowData, column, styleSheet}) => {
  return <CoverageSummaryNew
    criteria={value}
    column={column}
    rowData={rowData}
    styleSheet={styleSheet}
  />;
}

export const getHighlightRowCellRender = ({settings, styleSheet, value, rowData}) => {
  const documents = rowData.fields.find((field) => field.fieldName === 'documents');

  if (!documents) {
    return value;
  }

  if (documents.value.length === 1 && documents.value[0].documentTypes.length === 0) {
    return <HighlightRowCell
      settings={settings}
      styleSheet={styleSheet}
      value={value}
      rowData={rowData}
    />;
  }

  return value;
}

export const getPdfLinkCellRender = ({rowData, column, styleSheet}) => {
  return <PdfLink
    rowData={rowData}
    column={column}
    styleSheet={styleSheet}
  />;
};